import { sendsQuery, useCancelSendMutation, type EmailSend } from "@/api/campaigns";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { seo } from "@/utils/seo";
import { Box, Text, toast } from "@givenwell/components";
import { Tooltip } from "@/components/ui/Tooltip";
import { DotsIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { fallback, number, object, optional, pipe, toMinValue } from "valibot";

export const Route = createFileRoute("/campaigns/sends/")({
  component: SendsPage,
  validateSearch: object({
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
  }),
  head: () => ({ meta: seo({ title: "Email Sends" }) }),
});

const STATUS_COLORS: Record<string, string> = {
  draft: colors.text2,
  evaluating: "#6366f1",
  sending: "#f59e0b",
  completed: "#22c55e",
  failed: "#ef4444",
  cancelled: colors.text2,
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  draft: "Created but not yet started. The send is queued to begin shortly.",
  evaluating: "Evaluating the segment to determine the list of recipients.",
  sending: "Actively sending emails to recipients.",
  completed: "All emails have been processed and sent.",
  failed: "The send encountered an error and could not complete.",
  cancelled: "The send was cancelled before it completed.",
  scheduled: "Scheduled to send at a future date and time.",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Tooltip content={STATUS_DESCRIPTIONS[status]}>
      <Box
        css={{
          display: "inline-block",
          px: 8,
          py: 2,
          rounded: 999,
          fontScale: "xs",
          fontWeight: 500,
          color: STATUS_COLORS[status] || colors.text2,
          border: `1px solid ${STATUS_COLORS[status] || colors.outline}`,
          cursor: "default",
        }}
      >
        {status}
      </Box>
    </Tooltip>
  );
}

function SendsPage() {
  const search = Route.useSearch();
  const { page } = search;
  const query = useQuery(sendsQuery(page, 20));
  const navigate = useNavigate();
  const cancelMut = useCancelSendMutation();

  const rows = query.data?.items || [];

  return (
    <PageWrapper
      title="Email Sends"
      rightContent={
        <Button variant="tonal" color="primary" onClick={() => navigate({ to: "/campaigns/sends/new" })}>
          New Send
        </Button>
      }
    >
      <DataTable>
        <DataTableHeader hideToggle />
        <DataTableBody
          data={rows}
          status={query.status}
          dataKey="email_send_id"
          columns={[
            {
              header: "Name",
              cell: (send: EmailSend) => (
                <StyledLink to="/campaigns/sends/$sendId" params={{ sendId: send.email_send_id }}>
                  {send.name}
                </StyledLink>
              ),
            },
            {
              header: "Status",
              cell: (send: EmailSend) => <StatusBadge status={send.status} />,
            },
            {
              header: "Recipients",
              cell: (send: EmailSend) => <Text css={{ fontScale: "sm" }}>{send.total_recipients}</Text>,
            },
            {
              header: "Sent",
              cell: (send: EmailSend) => (
                <Text css={{ fontScale: "sm", color: (send.sent_count ?? 0) > 0 ? "#22c55e" : undefined }}>
                  {send.sent_count ?? 0}
                </Text>
              ),
            },
            {
              header: "Failed",
              cell: (send: EmailSend) => (
                <Text css={{ fontScale: "sm", color: (send.failed_count ?? 0) > 0 ? "#ef4444" : undefined }}>
                  {send.failed_count ?? 0}
                </Text>
              ),
            },
            {
              header: (
                <Tooltip content="Members who were already unsubscribed and did not receive this send.">
                  <span style={{ cursor: "default" }}>Skipped</span>
                </Tooltip>
              ),
              columnKey: "Skipped",
              cell: (send: EmailSend) => <Text css={{ fontScale: "sm" }}>{send.skipped_count ?? 0}</Text>,
            },
            {
              header: (
                <Tooltip content="Members who unsubscribed after receiving this send.">
                  <span style={{ cursor: "default" }}>Unsubscribed</span>
                </Tooltip>
              ),
              columnKey: "Unsubscribed",
              cell: (send: EmailSend) => <Text css={{ fontScale: "sm" }}>{send.unsubscribed_count ?? 0}</Text>,
            },
            {
              header: (
                <Tooltip content="Members who opened this email (tracking pixel loaded).">
                  <span style={{ cursor: "default" }}>Opened</span>
                </Tooltip>
              ),
              columnKey: "Opened",
              cell: (send: EmailSend) => (
                <Text css={{ fontScale: "sm" }}>{send.track_opens === false ? "N/A" : (send.opened_count ?? 0)}</Text>
              ),
            },
            {
              header: "Started",
              cell: (send: EmailSend) =>
                send.started_at ? (
                  new Date(send.started_at).toLocaleString()
                ) : (
                  <Text css={{ fontScale: "sm", color: colors.text2 }}>—</Text>
                ),
            },
            {
              header: "Created by",
              cell: (send: EmailSend) => send.created_by_name ?? "-",
            },
            {
              header: "Updated by",
              cell: (send: EmailSend) => send.updated_by_name ?? "-",
            },
            {
              header: "Created",
              cell: (send: EmailSend) => new Date(send.created_at).toLocaleDateString(),
            },
            {
              header: "",
              css: { align: "right" },
              cell: (send: EmailSend) => {
                const canCancel = send.status === "draft" || send.status === "evaluating";
                if (!canCancel) return null;
                return (
                  <DropdownMenu
                    align="end"
                    content={
                      <DropdownMenuItem
                        color="red"
                        onSelect={() => {
                          cancelMut.mutate(send.email_send_id, {
                            onSuccess: () => toast.success("Send cancelled"),
                          });
                        }}
                      >
                        Cancel
                      </DropdownMenuItem>
                    }
                  >
                    <IconButton aria-label="Actions" variant="plain" size="sm" color="neutral">
                      <DotsIcon style={{ color: colors.text2 }} />
                    </IconButton>
                  </DropdownMenu>
                );
              },
            },
          ]}
        />
        <DataTableFooter itemCount={query.data?.total_items} />
      </DataTable>
    </PageWrapper>
  );
}
